import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  ChangeDetectorRef,
  signal,
  computed,
} from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { combineLatest, distinctUntilChanged, map, Subscription, observeOn, asyncScheduler } from 'rxjs';
import {
  BreadcrumbActionsService,
  Logger,
  OrganizationDialogComponent,
  OrganizationService
} from 'src/app/@shared';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { AuthAdapterService } from 'pr1-ui-components';

const log = new Logger('ShellComponent');

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit {
  @ViewChild(MatSidenav) sidenav!: MatSidenav;

  viewModel$ = combineLatest([
    this.organizationService.organization$,
    this.authAdapterService.user$,
  ]).pipe(
    map(([organization, user]) => {
      const orgKey = organization?.name?.toLowerCase() as | keyof typeof environment.blobStorage | undefined;

      const blobConfig = orgKey && environment.blobStorage?.[orgKey]? (environment.blobStorage[orgKey] as {
            assetsUrl?: string;
            helpDocPath?: string;
        }) : {};

      const helpDocPath = blobConfig.helpDocPath?.trim() || '';

      const helpDocUrl = helpDocPath ? `${blobConfig.assetsUrl || ''}${helpDocPath}` : '';

      return {
        organization,
        user,
        helpDocUrl,
      };
    })
  );

  flexMediaWatcher!: Subscription;
  isMobile = false;
  appKey = environment.appKey;
  showBanner = false;
  readonly isMenuOpen = signal(true);
  readonly sidenavWidth = computed(() => this.isMenuOpen() ? 12.5 : 4);
  isOpen = true;
  breadcrumbActions$ = this.breadcrumbActionsService.actions$.pipe(
    observeOn(asyncScheduler),
  );

  constructor(
    private organizationService: OrganizationService,
    public dialog: MatDialog,
    private mediaObserver: MediaObserver,
    private authAdapterService: AuthAdapterService,
    private cdRef: ChangeDetectorRef,
    private breadcrumbActionsService: BreadcrumbActionsService,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  onBreadcrumbAction(id: string): void {
    this.breadcrumbActionsService.notifyAction(id);
  }

  ngOnInit(): void {
    log.debug('init');

    // detect changes in viewport size to handle left menu over or side
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        )
      )
      .subscribe((change) => {
        if (change.some((x) => x.mqAlias === 'lt-md')) {
          this.isMobile = true;
          if (this.sidenav) {
            this.sidenav.mode = 'over';
            this.sidenav.close();
          }
        } else {
          this.isMobile = false;
          if (this.sidenav) {
            this.sidenav.mode = 'side';
          }
        }
      });

    this.getShellBannerVisibility();
  }

  getShellBannerVisibility() {
    this.showBanner = this.organizationService.getShellBannerValue();
    this.cdRef.detectChanges();
    this.organizationService.shellBanner$.subscribe((res) => {
      this.showBanner = res;
      this.cdRef.detectChanges();
    });
  }

  logout(): void {
    this.authAdapterService.logout({ returnTo: this.doc.location.origin });
  }

  switchOrganization(): void {
    const dialogRef = this.dialog.open(OrganizationDialogComponent);
  }

  hideBanner() {
    this.showBanner = false;
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.flexMediaWatcher.unsubscribe();
  }
}
